import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
  const redirectUrl = window.location.origin + "/plan";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function GoogleButton({ label }) {
  return (
    <Button
      type="button"
      onClick={startGoogleAuth}
      variant="outline"
      className="w-full rounded-full h-12 border-slate-200 hover:bg-slate-50 btn-lift flex items-center justify-center gap-3"
      data-testid="google-auth-btn"
    >
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.5 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.1C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4.1-3.9 5.5l6.2 5.1c-.4.4 6.4-4.7 6.4-14.6 0-1.2-.1-2.4-.4-3.5z" />
      </svg>
      <span className="font-medium">{label}</span>
    </Button>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(form.email, form.password);
      toast.success("Willkommen zurück!");
      nav("/plan");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16 lg:py-24">
      <Card className="p-8 rounded-2xl border-slate-100">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Anmelden</h1>
        <p className="text-slate-600 mt-2 text-sm">Zugang zu deinem Ernährungsplan.</p>

        <div className="mt-6">
          <GoogleButton label="Mit Google anmelden" />
        </div>
        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <div className="flex-1 h-px bg-slate-200" />
          <span>oder mit E-Mail</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div>
            <Label htmlFor="email">E-Mail</Label>
            <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1.5 h-11" data-testid="login-email" />
          </div>
          <div>
            <Label htmlFor="password">Passwort</Label>
            <Input id="password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl mt-1.5 h-11" data-testid="login-password" />
          </div>
          {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 btn-lift" data-testid="login-submit">
            {loading ? "Anmelden..." : "Anmelden"}
          </Button>
        </form>
        <p className="text-sm text-slate-500 mt-6 text-center">
          Noch kein Konto? <Link to="/register" className="text-emerald-700 font-medium hover:underline">Registrieren</Link>
        </p>
      </Card>
    </div>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(form.email, form.password, form.name);
      toast.success("Konto erstellt.");
      nav("/paywall");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16 lg:py-24">
      <Card className="p-8 rounded-2xl border-slate-100">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Konto erstellen</h1>
        <p className="text-slate-600 mt-2 text-sm">In 30 Sekunden loslegen.</p>

        <div className="mt-6">
          <GoogleButton label="Mit Google fortfahren" />
        </div>
        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <div className="flex-1 h-px bg-slate-200" />
          <span>oder mit E-Mail</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="register-form">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5 h-11" data-testid="register-name" />
          </div>
          <div>
            <Label htmlFor="email">E-Mail</Label>
            <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1.5 h-11" data-testid="register-email" />
          </div>
          <div>
            <Label htmlFor="password">Passwort (min. 6 Zeichen)</Label>
            <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl mt-1.5 h-11" data-testid="register-password" />
          </div>
          {error && <div className="text-sm text-red-600" data-testid="register-error">{error}</div>}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 btn-lift" data-testid="register-submit">
            {loading ? "Erstelle Konto..." : "Konto erstellen"}
          </Button>
        </form>
        <p className="text-sm text-slate-500 mt-6 text-center">
          Bereits ein Konto? <Link to="/login" className="text-emerald-700 font-medium hover:underline">Anmelden</Link>
        </p>
      </Card>
    </div>
  );
}
